/* eslint-disable camelcase */
// @ts-check
import { Vector } from '@iabtechlabtcf/core'
import {
  isTCFVersion2_2,
  TCFVersion2_2_SupportedPurposeLI
} from '../utils/policyVersion'

/**
 * @param {PreferenceVectors} model
 * @returns {IABUserPrefs}
 */
function userPreferenceModel(model) {
  const setIfConsented =
    (id) =>
      (obj, [key, vector]) => {
        if (vector.has(id)) {
          obj[key] = true
        }
        return obj
      }
  const vectorToPrefArr = (vector) => {
    const pref = []
    vector.forEach((state, index) => {
      if (state) {
        pref.push(index)
      }
    })
    return pref
  }

  const consentedIds = (obj) =>
    Array.from(new Set(Object.values(obj).map(vectorToPrefArr).flat()))

  // preferences keys: consents, legInterest, optIn
  const idsForUserPreference = (preferences) => {
    const ids = consentedIds(preferences)
    return ids.reduce((acc, id) => {
      acc[id] = Object.entries(preferences).reduce(setIfConsented(id), {})
      return acc
    }, {})
  }

  // key: vendors, purposes, specialFeatures
  return Object.entries(model).reduce(
    (acc, [key, preferences]) => {
      acc[key] = idsForUserPreference(preferences)
      return acc
    },
    { vendors: {}, purposes: {}, specialFeatures: {} }
  )
}

/**
 * @param {UserPreferenceVectors} userPreferenceVectors
 * @returns {PreferenceVectors}
 */
const userPreferenceVectors = ({
  vendorConsents,
  vendorLegitimateInterests,
  purposeConsents,
  purposeLegitimateInterests,
  specialFeatureOptins
}) => {
  return {
    vendors: {
      consent: vendorConsents,
      legInterest: vendorLegitimateInterests
    },
    purposes: {
      consent: purposeConsents,
      legInterest: purposeLegitimateInterests
    },
    specialFeatures: {
      optIn: specialFeatureOptins
    }
  }
}

/**
 * @param {import('@iabtechlabtcf/core').TCModel} tcModel
 * @param {boolean} hasUserSelectedTheirPreference
 * @returns {{vendorLegitimateInterests: Vector, purposeLegitimateInterests: Vector}}
 */
function legitimateInterestPreferences(
  tcModel,
  hasUserSelectedTheirPreference
) {
  const defaultLegitimateInterests = () => {
    const getTCFV2_2LegIntPurposes = () =>
      Object.values(tcModel.gvl.purposes)
        .map(({ id }) => id)
        .filter((id) => TCFVersion2_2_SupportedPurposeLI.includes(id))

    const purposeLegitimateInterests = new Vector()

    const legIntPurposes = isTCFVersion2_2(tcModel.gvl.tcfPolicyVersion)
      ? getTCFV2_2LegIntPurposes()
      : tcModel.gvl.purposes

    purposeLegitimateInterests.set(legIntPurposes)
    const vendorLegitimateInterests = new Vector()

    const vendorLegitimateInterestsFromGVL = Object.values(tcModel.gvl.vendors).filter(
      (v) =>
        v.legIntPurposes.length > 0 &&
          !v.deletedDate
    ).map(({ id }) => id)
    vendorLegitimateInterests.set(vendorLegitimateInterestsFromGVL)
    return { vendorLegitimateInterests, purposeLegitimateInterests }
  }

  const userSelectedLegitimateInterests = () => {
    const toUnset = []
    tcModel.purposeLegitimateInterests.forEach((_, id) => {
      if (!TCFVersion2_2_SupportedPurposeLI.includes(id)) toUnset.push(id)
    })
    if (toUnset.length) tcModel.purposeLegitimateInterests.unset(toUnset)
    return {
      purposeLegitimateInterests: tcModel.purposeLegitimateInterests,
      vendorLegitimateInterests: tcModel.vendorLegitimateInterests
    }
  }

  return hasUserSelectedTheirPreference
    ? userSelectedLegitimateInterests()
    : defaultLegitimateInterests()
}

/**
 * @param {import('../ui/Ui').CmpModelForUi} cmpModel
 * @returns {UserPrefs}
 */
export function createUserPreference(cmpModel) {
  const { vendorConsents, purposeConsents, specialFeatureOptins } =
    cmpModel.tcModel
  const { vendorLegitimateInterests, purposeLegitimateInterests } =
    legitimateInterestPreferences(
      cmpModel.tcModel,
      cmpModel.hasUserSelectedTheirPreference
    )
  const iabUserPrefs = userPreferenceModel(
    userPreferenceVectors({
      vendorConsents,
      vendorLegitimateInterests,
      purposeConsents,
      purposeLegitimateInterests,
      specialFeatureOptins
    })
  )
  return {
    ...iabUserPrefs,
    nonTcfVendors: cmpModel.acmVendors.consents || {},
    nonRegisteredVendors: cmpModel.nonRegisteredVendors.consents || {}
  }
}

/**
 * @typedef {Object} IABUserPrefs
 * @property {PurposePreference} purposes
 * @property {SpecialFeaturePreference} specialFeatures
 * @property {VendorsPreference} vendors
 */

/**
 * @typedef {Object} NonIABUserPrefs
 * @property {import('../cmp/NonIABModel').Consents} nonTcfVendors
 * @property {import('../cmp/NonIABModel').Consents} nonRegisteredVendors
 */

/** @typedef {IABUserPrefs & NonIABUserPrefs} UserPrefs */

/**
 * @typedef {Object} Preference
 * @property {boolean} [consent]
 * @property {boolean} [legInterest]
 */

/** @typedef {Record<number, Preference} VendorsPreference */
/** @typedef {Record<number,Preference>} PurposePreference */
/** @typedef {Record<number, {optIn: boolean}>} SpecialFeaturePreference */

/**
 * @typedef {Object} UserPreferenceVectors
 * @property {import('@iabtechlabtcf/core').Vector} vendorConsents
 * @property {import('@iabtechlabtcf/core').Vector} vendorLegitimateInterests
 * @property {import('@iabtechlabtcf/core').Vector} purposeConsents
 * @property {import('@iabtechlabtcf/core').Vector} purposeLegitimateInterests
 * @property {import('@iabtechlabtcf/core').Vector} specialFeatureOptins
 */

/**
 * @typedef {Object} PreferenceVectors
 * @property {{consent: Vector, legInterest: Vector}} vendors
 * @property {{consent: Vector, legInterest: Vector}} purposes
 * @property {{optIn: Vector}} specialFeatures
 */
